const JYO_MAP = {
  '01': '札幌',
  '02': '函館',
  '03': '福島',
  '04': '新潟',
  '05': '東京',
  '06': '中山',
  '07': '中京',
  '08': '京都',
  '09': '阪神',
  '10': '小倉',
};

const MARKS = ['◎', '○', '▲'];
const BET_TYPE_PRIORITY = [
  { key: 'sanrentan_df', label: '3連単', ordered: true, priority: 0 },
  { key: 'sanrenpuku_df', label: '3連複', ordered: false, priority: 1 },
  { key: 'umaren_df', label: '馬連', ordered: false, priority: 2 },
];

// AI指数の注記。数値が出る画面には必ず1回出す（景表法・優良誤認対策、#72）。
//
// 「期待値」ではなく「AI指数」と呼ぶのは、実測でこの数値が高い馬ほど回収率が低いと
// 分かっているため。「期待値」では大小の意味が逆に伝わり続ける。
// 実測（2019-2026 の8年ウォークフォワード、docs/issues/16-recovery-rate-label.md）:
//   指数1.0以上 -> 実回収率 0.756 ／ 指数2.0以上 -> 実回収率 0.632
export const AI_INDEX_NOTE =
  'AI指数は「AIの予測確率 × オッズ」で算出した発走前の目安値です。'
  + '過去の実績回収率ではありません。'
  + 'この数値が高いほど当たりやすい／儲かることを示すものではなく、'
  + '実測では指数が高いほど回収率は低い傾向があります。';

export const formatRaceLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const normalized = raw.replace('Ｒ', 'R');
  const match = normalized.match(/^([^\d]+)\s*(\d+)\s*R?$/);
  if (match) return `${match[1].trim()}${Number(match[2])}R`;

  const digits = normalized.replace(/\D/g, '');
  if (digits.length >= 10) {
    const jyo = JYO_MAP[digits.slice(8, 10)];
    const raceNo = Number(digits.slice(-2));
    if (jyo && Number.isFinite(raceNo) && raceNo > 0) return `${jyo}${raceNo}R`;
  }
  return raw;
};

export const toNumber = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const normalized =
    typeof value === 'string' ? value.replace(/,/g, '').replace(/\s+/g, '') : value;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
};

const formatFixed = (value, digits = 1) => {
  const num = toNumber(value);
  return num === null ? '-' : num.toFixed(digits);
};

export const formatPercent = (value, digits = 1) => {
  const num = toNumber(value);
  if (num === null) return '-';
  return `${num.toFixed(digits)}%`;
};

export const formatOdds = (value) => {
  const num = toNumber(value);
  return num === null ? '-' : `${num.toFixed(1)}倍`;
};

export const formatPlainNumber = (value, digits = 1) => {
  const num = toNumber(value);
  return num === null ? '-' : num.toFixed(digits);
};

export const formatMultiplier = (value, digits = 2) => {
  const num = toNumber(value);
  return num === null ? '-' : num.toFixed(digits);
};

export const formatYen = (value) => {
  const num = toNumber(value);
  return num === null ? '-' : `${Math.round(num).toLocaleString()}円`;
};

export const formatBodyWeight = (weight, diff) => {
  const weightNum = toNumber(weight);
  if (weightNum === null) return '-';
  const diffNum = toNumber(diff);
  const body = `${Math.round(weightNum)}`;
  if (diffNum === null) return body;
  if (diffNum > 0) return `${body}(+${Math.round(diffNum)})`;
  if (diffNum < 0) return `${body}(${Math.round(diffNum)})`;
  return `${body}(0)`;
};

export const formatFinishPosition = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  const num = toNumber(value);
  if (num !== null) return num > 0 ? `${Math.round(num)}` : '-';
  return String(value);
};

const normalizeUnitProbability = (value) => {
  const num = toNumber(value);
  if (num === null) return 0;
  if (num <= 1) return Math.max(num, 0);
  if (num <= 100) return Math.max(num / 100, 0);
  return 0;
};

const toPercentProbability = (value) => Number((normalizeUnitProbability(value) * 100).toFixed(1));

const buildQuinellaRates = (unitProbabilities) => {
  const total = unitProbabilities.reduce((sum, value) => sum + value, 0);
  if (total <= 0) {
    return unitProbabilities.map(() => 0);
  }

  const normalized = unitProbabilities.map((value) => value / total);
  return normalized.map((target, index) => {
    let placeSecond = 0;
    normalized.forEach((other, otherIndex) => {
      if (index === otherIndex) return;
      const remain = 1 - other;
      if (remain > 0) {
        placeSecond += other * (target / remain);
      }
    });
    return Number(((target + placeSecond) * 100).toFixed(1));
  });
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const confidenceToneMap = {
  A: 'a',
  B: 'b',
  C: 'c',
};

const buildRankMap = (rows, selector) => {
  const sorted = [...rows].sort((a, b) => {
    const av = selector(a);
    const bv = selector(b);
    if (av === bv) return (a.horseNumber ?? 0) - (b.horseNumber ?? 0);
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    return bv - av;
  });

  return new Map(sorted.map((row, index) => [row.horseNumber, index + 1]));
};

const comboLabel = (row, ordered = false) => {
  const values = [row?.umaban_a, row?.umaban_b, row?.umaban_c]
    .filter((value) => value !== undefined && value !== null && value !== '')
    .map((value) => String(value));
  if (!values.length) return '-';
  return ordered ? values.join(' - ') : [...values].sort((a, b) => Number(a) - Number(b)).join(' - ');
};

const buildBetCandidates = (data) =>
  BET_TYPE_PRIORITY.flatMap((betType) =>
    (data?.[betType.key] || []).map((row) => ({
      betType: betType.label,
      typeKey: betType.key,
      priority: betType.priority,
      ordered: betType.ordered,
      row,
      combination: [row?.umaban_a, row?.umaban_b, row?.umaban_c]
        .filter((value) => value !== undefined && value !== null && value !== '')
        .map((value) => String(value)),
      combinationText: comboLabel(row, betType.ordered),
      probPct: toNumber(row?.prob_pct) ?? (() => {
        const prob = toNumber(row?.prob);
        return prob === null ? null : Number((prob * 100).toFixed(2));
      })(),
      odds: toNumber(row?.odds),
      ev: toNumber(row?.ev),
      stakeYen: toNumber(row?.stake_yen),
      expectedReturnYen: toNumber(row?.expected_return_yen),
      rank: toNumber(row?.rank) ?? Number.MAX_SAFE_INTEGER,
    }))
  );

const pickFinalPrediction = (data, focusHorses) => {
  const candidates = buildBetCandidates(data);
  const byPriority = BET_TYPE_PRIORITY.flatMap((betType) => {
    const rows = candidates
      .filter((candidate) => candidate.typeKey === betType.key && candidate.odds !== null)
      .sort((a, b) => {
        const aRecommended = (a.stakeYen ?? 0) > 0 ? 1 : 0;
        const bRecommended = (b.stakeYen ?? 0) > 0 ? 1 : 0;
        if (aRecommended !== bRecommended) return bRecommended - aRecommended;
        const returnDiff = (b.expectedReturnYen ?? -1) - (a.expectedReturnYen ?? -1);
        if (returnDiff !== 0) return returnDiff;
        const evDiff = (b.ev ?? -1) - (a.ev ?? -1);
        if (evDiff !== 0) return evDiff;
        return a.rank - b.rank;
      });
    return rows.slice(0, 1);
  });

  const fallback = [...candidates].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    const evDiff = (b.ev ?? -1) - (a.ev ?? -1);
    if (evDiff !== 0) return evDiff;
    const probDiff = (b.probPct ?? -1) - (a.probPct ?? -1);
    if (probDiff !== 0) return probDiff;
    return a.rank - b.rank;
  })[0];

  const selected = byPriority[0] || fallback;
  if (!selected) {
    return {
      betType: '3連単',
      combination: focusHorses.slice(0, 3).map((horse) => String(horse.horseNumber)),
      combinationText: focusHorses.slice(0, 3).map((horse) => horse.horseNumber).join(' - '),
      oddsLabel: 'オッズ取得待ち',
      oddsHeading: '想定オッズ',
      payoutHeading: '的中時の払戻',
      expectedPayout: '-',
      payoutNote: '買い目データから算出できませんでした。',
      confidenceLabel: '-',
      stakeLabel: '-',
      note: '勝率上位3頭をベースに暫定表示しています。',
    };
  }

  const oddsPool = candidates
    .filter((candidate) => candidate.typeKey === selected.typeKey && candidate.odds !== null)
    .slice(0, 5)
    .map((candidate) => candidate.odds);
  const oddsMin = oddsPool.length ? Math.min(...oddsPool) : null;
  const oddsMax = oddsPool.length ? Math.max(...oddsPool) : null;
  const hasOddsRange = oddsMin !== null && oddsMax !== null && oddsMax > oddsMin;
  const payoutBase =
    selected.expectedReturnYen !== null && selected.expectedReturnYen > 0
      ? selected.expectedReturnYen
      : null;
  const fallbackPayout =
    selected.odds !== null
      ? selected.odds * Math.max(selected.stakeYen ?? 100, 100)
      : null;

  return {
    betType: selected.betType,
    combination: selected.combination,
    combinationText: selected.combinationText,
    oddsHeading: hasOddsRange ? '想定オッズ帯' : '想定オッズ',
    oddsLabel:
      oddsMin === null
        ? 'オッズ取得待ち'
        : hasOddsRange
          ? `${formatFixed(oddsMin, 1)}倍〜${formatFixed(oddsMax, 1)}倍`
          : `${formatFixed(selected.odds ?? oddsMin, 1)}倍`,
    // 2 つの経路で意味が違う値が入るので、見出しも経路に合わせる（#72）。
    //   payoutBase   = 賭け金 × AI指数（build_exotic_bets.py の stake_yen * ev）… 見込み値
    //   fallbackPayout = オッズ × 賭け金 … 的中したときに実際に返る額
    payoutHeading: payoutBase !== null ? 'AI指数ベースの想定払戻' : '的中時の払戻',
    expectedPayout: formatYen(payoutBase ?? fallbackPayout),
    payoutNote:
      payoutBase !== null
        ? '買い目データのAI指数から算出した見込み額です。実績の払戻ではありません。'
        : fallbackPayout !== null
          ? '推奨配分が未計算のため、的中時の払戻を100円換算で表示'
          : '払戻はオッズ取得後に表示されます。',
    confidenceLabel: selected.probPct === null ? '-' : formatPercent(selected.probPct, 2),
    stakeLabel: selected.stakeYen === null || selected.stakeYen === 0 ? '-' : formatYen(selected.stakeYen),
    note: '既存の買い目データから最も見せ場のある候補を抽出しています。',
  };
};

const rankToScore = (rank, total) => {
  if (!rank || total <= 1) return 0;
  return clamp((total - rank) / (total - 1), 0, 1);
};

const deriveRaceConfidence = (rows, totalProbabilityMass) => {
  const top3SharePct = [...rows]
    .sort((a, b) => (b.winSharePct ?? 0) - (a.winSharePct ?? 0))
    .slice(0, 3)
    .reduce((sum, row) => sum + (row.winSharePct ?? 0), 0);

  const massScore = clamp((totalProbabilityMass - 1.45) / 0.8, 0, 1);
  const concentrationScore = clamp((top3SharePct - 45) / 20, 0, 1);
  const confidenceScore = massScore * 0.55 + concentrationScore * 0.45;

  if (confidenceScore >= 0.7) {
    return { label: 'A', tone: confidenceToneMap.A, score: confidenceScore, top3SharePct };
  }
  if (confidenceScore >= 0.4) {
    return { label: 'B', tone: confidenceToneMap.B, score: confidenceScore, top3SharePct };
  }
  return { label: 'C', tone: confidenceToneMap.C, score: confidenceScore, top3SharePct };
};

const buildHorseRows = (data) => {
  const baseRows = (data?.tanpuku_df || [])
    .map((row) => ({
      horseNumber: row?.umaban,
      horseName: row?.horse_name && row.horse_name !== '-' ? row.horse_name : `馬番 ${row?.umaban ?? '-'}`,
      jockeyName: row?.jockey_name || '-',
      odds: toNumber(row?.tansho_odds),
      unitProbability: normalizeUnitProbability(row?.p),
      winRate: toPercentProbability(row?.p),
      // CSV は `edge`(=EV) 列で出力される（v13/top1 とも）。`ev` 優先・無ければ `edge` にフォールバック。
      expectedValue: toNumber(row?.ev ?? row?.edge),
      recommended: Number(row?.pick) === 1,
      baseOdds: toNumber(row?.base_odds),
      weight: row?.futan_juryo,
      bodyWeight: row?.bataiju,
      bodyWeightDiff: row?.zogen_sa,
      finishPosition: row?.finish_position,
      runningStyle: row?.running_style || '-',
      abilityIndex: toNumber(row?.compi_index),
      pRank: toNumber(row?.p_rank_in_race),
    }))
    .filter((row) => row.horseNumber !== undefined && row.horseNumber !== null)
    .sort((a, b) => {
      const rankDiff = (a.pRank ?? Number.MAX_SAFE_INTEGER) - (b.pRank ?? Number.MAX_SAFE_INTEGER);
      if (rankDiff !== 0) return rankDiff;
      return (b.winRate ?? 0) - (a.winRate ?? 0);
    });

  const totalProbabilityMass = baseRows.reduce((sum, row) => sum + (row.unitProbability ?? 0), 0);
  const rowsWithShare = baseRows.map((row) => ({
    ...row,
    winSharePct:
      totalProbabilityMass > 0
        ? Number((((row.unitProbability ?? 0) / totalProbabilityMass) * 100).toFixed(1))
        : 0,
  }));

  const shareRankMap = buildRankMap(rowsWithShare, (row) => row.winSharePct);
  const evRankMap = buildRankMap(rowsWithShare, (row) => row.expectedValue);
  const abilityRankMap = buildRankMap(rowsWithShare, (row) => row.abilityIndex);
  const total = rowsWithShare.length;

  const rowsWithScore = rowsWithShare.map((row) => {
    const shareRank = shareRankMap.get(row.horseNumber);
    const evRank = evRankMap.get(row.horseNumber);
    const abilityRank = abilityRankMap.get(row.horseNumber);
    const compositeScore =
      rankToScore(shareRank, total) * 0.6 +
      rankToScore(evRank, total) * 0.3 +
      rankToScore(abilityRank, total) * 0.1;
    return {
      ...row,
      shareRank,
      evRank,
      abilityRank,
      compositeScore: Number(compositeScore.toFixed(4)),
    };
  });

  const recommendedRows = [...rowsWithScore]
    .filter((row) => row.recommended)
    .sort((a, b) => {
      const diff = b.compositeScore - a.compositeScore;
      if (diff !== 0) return diff;
      return (b.winSharePct ?? 0) - (a.winSharePct ?? 0);
    });
  const recommendedRankMap = new Map(
    recommendedRows.map((row, index) => [row.horseNumber, index + 1])
  );
  const scorePriorityRows = [...rowsWithScore].sort((a, b) => {
    const scoreDiff = b.compositeScore - a.compositeScore;
    if (scoreDiff !== 0) return scoreDiff;
    const winRateDiff = (b.winRate ?? 0) - (a.winRate ?? 0);
    if (winRateDiff !== 0) return winRateDiff;
    return (a.horseNumber ?? 0) - (b.horseNumber ?? 0);
  });
  const topWinHorseNumber =
    [...rowsWithScore]
      .sort((a, b) => {
        const diff = (b.winRate ?? 0) - (a.winRate ?? 0);
        if (diff !== 0) return diff;
        return (a.horseNumber ?? 0) - (b.horseNumber ?? 0);
      })[0]?.horseNumber ?? null;
  const overallCompositeRankMap = new Map(
    [...rowsWithScore]
      .sort((a, b) => {
        const diff = b.compositeScore - a.compositeScore;
        if (diff !== 0) return diff;
        return (b.winSharePct ?? 0) - (a.winSharePct ?? 0);
      })
      .map((row, index) => [row.horseNumber, index + 1])
  );
  const legacyRankByHorseNumber = new Map();

  const quinellaRates = buildQuinellaRates(rowsWithScore.map((row) => row.unitProbability ?? 0));
  rowsWithScore.forEach((row) => {
    const recommendedRank = recommendedRankMap.get(row.horseNumber);
    let legacyAiRank = 'D';

    if (recommendedRows.length > 0) {
      if (row.recommended) {
        if (recommendedRank === 1) {
          legacyAiRank = 'S';
        } else if (recommendedRank <= Math.max(2, Math.ceil(recommendedRows.length * 0.4))) {
          legacyAiRank = 'A';
        } else {
          legacyAiRank = 'B';
        }
      } else if (
        (row.shareRank ?? Number.MAX_SAFE_INTEGER) <= 3 ||
        (row.evRank ?? Number.MAX_SAFE_INTEGER) <= 3
      ) {
        legacyAiRank = 'C';
      }
    } else {
      const overallRank = overallCompositeRankMap.get(row.horseNumber) ?? Number.MAX_SAFE_INTEGER;
      if (overallRank === 1) legacyAiRank = 'S';
      else if (overallRank === 2) legacyAiRank = 'A';
      else if (overallRank <= Math.max(3, Math.ceil(total * 0.25))) legacyAiRank = 'B';
      else if (overallRank <= Math.max(6, Math.ceil(total * 0.55))) legacyAiRank = 'C';
    }

    legacyRankByHorseNumber.set(row.horseNumber, legacyAiRank);
  });

  const pickHorseNumberByLegacyRanks = (preferredRanks, excludedHorseNumbers = new Set()) =>
    scorePriorityRows.find(
      (row) =>
        !excludedHorseNumbers.has(row.horseNumber) &&
        preferredRanks.includes(legacyRankByHorseNumber.get(row.horseNumber))
    )?.horseNumber ?? null;

  const selectedHorseNumbers = new Set();
  if (topWinHorseNumber !== null) {
    selectedHorseNumbers.add(topWinHorseNumber);
  }
  const secondRankHorseNumber =
    pickHorseNumberByLegacyRanks(['S'], selectedHorseNumbers) ??
    pickHorseNumberByLegacyRanks(['A'], selectedHorseNumbers) ??
    pickHorseNumberByLegacyRanks(['B'], selectedHorseNumbers);
  if (secondRankHorseNumber !== null) {
    selectedHorseNumbers.add(secondRankHorseNumber);
  }
  const thirdRankHorseNumber =
    pickHorseNumberByLegacyRanks(['A'], selectedHorseNumbers) ??
    pickHorseNumberByLegacyRanks(['B'], selectedHorseNumbers) ??
    pickHorseNumberByLegacyRanks(['C'], selectedHorseNumbers);

  const horses = rowsWithScore.map((row, index) => {
    const mark = MARKS[index] || '';
    const hasPositiveExpectedValue = row.expectedValue !== null && row.expectedValue >= 1;
    let aiRank = 'D';

    if (row.horseNumber === topWinHorseNumber) {
      aiRank = 'S';
    } else if (row.horseNumber === secondRankHorseNumber) {
      aiRank = 'A';
    } else if (row.horseNumber === thirdRankHorseNumber) {
      aiRank = 'B';
    } else if (hasPositiveExpectedValue) {
      aiRank = 'C';
    }

    return {
      ...row,
      rank: index + 1,
      mark,
      quinellaRate: quinellaRates[index] ?? 0,
      aiRank,
    };
  });

  return {
    horses,
    totalProbabilityMass,
    raceConfidence: deriveRaceConfidence(horses, totalProbabilityMass),
  };
};

export const CHART_HORSE_LIMIT = 8;

// 買い目（○）を色分けするグラフ用（中央新版）。勝率上位 8 頭に、8 頭から外れた ○ の馬を足す。
// ○ は単勝 8〜100 倍の穴馬に付くので、上位 8 頭だけだと ○ がグラフに出ないレースがある。
// horses は勝率の高い順に並んでいる前提で、その順番を保つ。
export const buildPickChartData = (horses, limit = CHART_HORSE_LIMIT) => {
  if (!Array.isArray(horses)) return [];
  const top = horses.slice(0, limit);
  const hiddenPicks = horses.slice(limit).filter((horse) => horse?.recommended);
  return [...top, ...hiddenPicks];
};

export const buildRaceDetailModel = ({ data, selectedRaceMeta, selectedDate, aiRankOverride }) => {
  const horseResult = buildHorseRows(data);
  const baseHorses = horseResult?.horses ?? [];
  if (!baseHorses.length) return null;
  const horses = typeof aiRankOverride === 'function' ? aiRankOverride(baseHorses) : baseHorses;

  const raceLabel = formatRaceLabel(data?.race_name || selectedRaceMeta?.race_name || selectedRaceMeta?.race_key);
  const surfaceLabel =
    selectedRaceMeta?.surface_label ||
    (selectedRaceMeta?.surface === 'shiba' ? '芝' : selectedRaceMeta?.surface === 'dart' ? 'ダート' : null);
  const roughness = selectedRaceMeta?.roughness_raw || selectedRaceMeta?.roughness || null;
  const raceConfidenceLabel =
    selectedRaceMeta?.confidence_raw || selectedRaceMeta?.confidence || horseResult?.raceConfidence?.label || null;
  const raceConfidence = raceConfidenceLabel
    ? {
        ...(horseResult?.raceConfidence ?? {}),
        label: raceConfidenceLabel,
        tone: confidenceToneMap[raceConfidenceLabel] || horseResult?.raceConfidence?.tone || confidenceToneMap.C,
      }
    : null;
  const probabilityTopHorses = horses.slice(0, 3);
  const focusHorses = [...horses]
    .sort((a, b) => {
      const evDiff = (b.expectedValue ?? Number.NEGATIVE_INFINITY) - (a.expectedValue ?? Number.NEGATIVE_INFINITY);
      if (evDiff !== 0) return evDiff;
      const winRateDiff = (b.winRate ?? 0) - (a.winRate ?? 0);
      if (winRateDiff !== 0) return winRateDiff;
      return (a.horseNumber ?? 0) - (b.horseNumber ?? 0);
    })
    .slice(0, 3)
    .map((horse, index) => ({
      ...horse,
      focusMark: MARKS[index] || '',
    }));
  const recommendedHorses = [...horses]
    .filter((horse) => ['S', 'A', 'B'].includes(horse.aiRank))
    .sort((a, b) => {
      const rankPriority = { S: 0, A: 1, B: 2 };
      const rankDiff = (rankPriority[a.aiRank] ?? 99) - (rankPriority[b.aiRank] ?? 99);
      if (rankDiff !== 0) return rankDiff;
      const winRateDiff = (b.winRate ?? 0) - (a.winRate ?? 0);
      if (winRateDiff !== 0) return winRateDiff;
      return (a.horseNumber ?? 0) - (b.horseNumber ?? 0);
    })
    .slice(0, 3);
  const finalPrediction = pickFinalPrediction(data, probabilityTopHorses);
  const topHorse = horses[0];
  const top3OddsSum =
    probabilityTopHorses.length === 3 && probabilityTopHorses.every((horse) => horse.odds !== null)
      ? probabilityTopHorses.reduce((sum, horse) => sum + (horse.odds ?? 0), 0)
      : null;

  return {
    breadcrumbItems: ['中央競馬', raceLabel],
    title: `${raceLabel}のAI分析`,
    subtitle: 'AIデータ解析による予想分析',
    raceLabel,
    raceDate: selectedDate,
    surfaceLabel,
    roughness,
    raceConfidence,
    horses,
    recommendedHorses,
    focusHorses,
    chartData: horses.slice(0, CHART_HORSE_LIMIT),
    finalPrediction,
    stats: [
      { label: '解析頭数', value: `${horses.length}頭` },
      { label: 'トップ勝率', value: formatPercent(topHorse?.winRate, 1) },
      { label: '上位3頭オッズ合計', value: formatPlainNumber(top3OddsSum, 1) },
      { label: '上位3頭シェア', value: formatPercent(raceConfidence?.top3SharePct, 1) },
    ],
  };
};

const toPerformanceEntry = (mark, horse) => {
  if (!horse) return null;
  const finishPosition = formatFinishPosition(horse.finishPosition);
  return {
    mark,
    horseNumber: horse.horseNumber,
    horseName: horse.horseName,
    oddsLabel: formatOdds(horse.odds),
    finishPosition,
    finishLabel: finishPosition === '-' ? '未確定' : `${finishPosition}着`,
  };
};

const toMarkLabel = (horse) => {
  if (!horse) return '無印';
  if (horse.aiRank === 'S') return '◎';
  if (horse.aiRank === 'A') return '○';
  if (horse.aiRank === 'B') return '▲';
  if (horse.aiRank === 'C') return '△';
  return '無印';
};

const normalizeHorseNumberKey = (value) => {
  const num = toNumber(value);
  if (num !== null) return String(Math.round(num));
  return String(value ?? '').trim();
};

const findComboOddsLabel = (rows, combo, ordered = false) => {
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(combo) || !combo.length) return '-';
  const target = combo.map((value) => normalizeHorseNumberKey(value)).filter(Boolean);
  if (!target.length) return '-';
  const normalizedTarget = ordered ? target : [...target].sort((a, b) => Number(a) - Number(b));

  const matched = rows.find((row) => {
    const rowCombo = [row?.umaban_a, row?.umaban_b, row?.umaban_c]
      .map((value) => normalizeHorseNumberKey(value))
      .filter(Boolean);
    if (rowCombo.length !== normalizedTarget.length) return false;
    const normalizedRow = ordered ? rowCombo : [...rowCombo].sort((a, b) => Number(a) - Number(b));
    return normalizedRow.every((value, index) => value === normalizedTarget[index]);
  });

  return matched ? formatOdds(matched?.odds) : '-';
};

const toPodiumEntry = (position, horse) => {
  if (!horse) return null;
  return {
    position,
    horseNumber: horse.horseNumber,
    horseName: horse.horseName,
    mark: toMarkLabel(horse),
    oddsLabel: formatOdds(horse.odds),
    finishLabel: `${position}着`,
  };
};

export const buildRacePerformanceSummary = ({ data, selectedRaceMeta, selectedDate, aiRankOverride }) => {
  const model = buildRaceDetailModel({ data, selectedRaceMeta, selectedDate, aiRankOverride });
  if (!model?.horses?.length) return null;

  const horses = model.horses;
  const sHorse = horses.find((horse) => horse.aiRank === 'S');
  const aHorse = horses.find((horse) => horse.aiRank === 'A');
  const bHorse = horses.find((horse) => horse.aiRank === 'B');
  const cHorses = horses.filter((horse) => horse.aiRank === 'C');

  const entries = [
    toPerformanceEntry('◎', sHorse),
    toPerformanceEntry('○', aHorse),
    toPerformanceEntry('▲', bHorse),
    ...cHorses.map((horse) => toPerformanceEntry('△', horse)),
  ].filter(Boolean);

  const podiumEntries = [...horses]
    .filter((horse) => {
      const finish = toNumber(horse.finishPosition);
      return finish !== null && finish > 0;
    })
    .sort((a, b) => {
      const aFinish = toNumber(a.finishPosition) ?? Number.MAX_SAFE_INTEGER;
      const bFinish = toNumber(b.finishPosition) ?? Number.MAX_SAFE_INTEGER;
      if (aFinish !== bFinish) return aFinish - bFinish;
      return (a.horseNumber ?? 0) - (b.horseNumber ?? 0);
    })
    .slice(0, 3)
    .map((horse, index) => toPodiumEntry(index + 1, horse))
    .filter(Boolean);

  const top2Combo = podiumEntries.slice(0, 2).map((entry) => entry.horseNumber);
  const top3Combo = podiumEntries.slice(0, 3).map((entry) => entry.horseNumber);

  return {
    raceKey: selectedRaceMeta?.race_key || model.raceLabel,
    raceName: model.raceLabel,
    entries,
    podiumEntries,
    resultOdds: {
      umaren: top2Combo.length === 2 ? findComboOddsLabel(data?.umaren_df, top2Combo, false) : '-',
      sanrenpuku:
        top3Combo.length === 3 ? findComboOddsLabel(data?.sanrenpuku_df, top3Combo, false) : '-',
      sanrentan:
        top3Combo.length === 3 ? findComboOddsLabel(data?.sanrentan_df, top3Combo, true) : '-',
    },
    hasConfirmedResult:
      podiumEntries.length > 0 || entries.some((entry) => entry.finishPosition !== '-'),
  };
};
